import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import BusyIndicatorHTML from "../components/BusyIndicator/BusyIndicatorHTML";
import type { BusyIndicatorOptions } from "../models/BusyIndicatorOptions";

interface BusyScopeState {
  count: number;
  message?: string;
}

type BusyScopes = Record<string, BusyScopeState>;

interface BusyIndicatorContextValue {
  showBusyIndicator: (options?: BusyIndicatorOptions) => void;
  closeBusyIndicator: (scope?: string) => void;
  closeAllBusyIndicators: () => void;
  isBusy: (scope?: string) => boolean;
  getBusyMessage: (scope?: string) => string | undefined;
}

interface BusyIndicatorProviderProps {
  children: ReactNode;
}

export const GlobalBusyScope = "global";

const BusyIndicatorContext = createContext<
  BusyIndicatorContextValue | undefined
>(undefined);

export function BusyIndicatorProvider({
  children,
}: BusyIndicatorProviderProps) {
  const [scopes, setScopes] = useState<BusyScopes>({});

  const showBusyIndicator = useCallback(
    (options?: BusyIndicatorOptions): void => {
      const scope = options?.scope ?? GlobalBusyScope;

      setScopes((previousScopes) => {
        const currentScope = previousScopes[scope];

        return {
          ...previousScopes,
          [scope]: {
            count: (currentScope?.count ?? 0) + 1,
            message: options?.message ?? currentScope?.message,
          },
        };
      });
    },
    [],
  );

  const closeBusyIndicator = useCallback(
    (scope: string = GlobalBusyScope): void => {
      setScopes((previousScopes) => {
        const currentScope = previousScopes[scope];

        if (!currentScope) {
          return previousScopes;
        }

        const nextCount = Math.max(0, currentScope.count - 1);

        if (nextCount > 0) {
          return {
            ...previousScopes,
            [scope]: {
              ...currentScope,
              count: nextCount,
            },
          };
        }

        const nextScopes = { ...previousScopes };
        delete nextScopes[scope];
        return nextScopes;
      });
    },
    [],
  );

  const closeAllBusyIndicators = useCallback((): void => {
    setScopes({});
  }, []);

  const isBusy = useCallback(
    (scope: string = GlobalBusyScope): boolean =>
      (scopes[scope]?.count ?? 0) > 0,
    [scopes],
  );

  const getBusyMessage = useCallback(
    (scope: string = GlobalBusyScope): string | undefined =>
      scopes[scope]?.message,
    [scopes],
  );

  const value = useMemo<BusyIndicatorContextValue>(
    () => ({
      showBusyIndicator,
      closeBusyIndicator,
      closeAllBusyIndicators,
      isBusy,
      getBusyMessage,
    }),
    [
      showBusyIndicator,
      closeBusyIndicator,
      closeAllBusyIndicators,
      isBusy,
      getBusyMessage,
    ],
  );

  return (
    <BusyIndicatorContext.Provider value={value}>
      {children}
      <BusyIndicatorHTML scope={GlobalBusyScope} mode="overlay" />
    </BusyIndicatorContext.Provider>
  );
}

export function useBusyIndicator(): BusyIndicatorContextValue {
  const context = useContext(BusyIndicatorContext);

  if (!context) {
    throw new Error(
      "useBusyIndicator must be used inside BusyIndicatorProvider.",
    );
  }

  return context;
}
